/**
 * Sales Agent settings, as the API serves them and the Settings and Discount Limits tabs use them:
 * the settings that apply to the selected brand, its own or the account's, with every field
 * always filled in.
 */

/** The longest disclaimer the API accepts. */
export const DISCLAIMER_MAX_LENGTH = 2000;

/** The longest conversation starter the API accepts. There is no limit on how many. */
export const STARTER_QUESTION_MAX_LENGTH = 80;

/** The longest subtitle the API accepts, and what the chat shows when it is left empty. */
export const SUBTITLE_MAX_LENGTH = 60;
export const DEFAULT_SUBTITLE = 'Your AI Assistant';

export interface Disclaimer {
	/** True: the shopper must agree before sending a message. False: footer link only. */
	enabled: boolean;
	/** Markdown subset: **bold**, *italic* and [links](https://…). */
	text: string;
}

/** Where the agent's discount codes come from: the brand's own, or made in Shopify per offer. */
export type DiscountCodeSource = 'existing' | 'generate';

/** Shopify products, tags and collections the agent never discounts. */
export interface DiscountExclusions {
	productIds: string[];
	tags: string[];
	collectionIds: string[];
}

/** The Discount Limits tab. */
export interface DiscountSettings {
	/** Off: the agent never offers a discount. The limits are kept either way. */
	enabled: boolean;
	source: DiscountCodeSource;
	/** The brand's own Shopify codes; used when source is existing. */
	codes: string[];
	/** The highest percentage off a single order. Null: no percentage maximum. */
	maxPercent: number | null;
	/** The most money off a single order. Null: no money cap. */
	maxAmount: number | null;
	/** At most `count` discounts in `days`. */
	frequency: { count: number; days: number };
	exclusions: DiscountExclusions;
}

/** The longest code Shopify accepts, and how many codes the API takes. */
export const DISCOUNT_CODE_MAX_LENGTH = 255;
export const DISCOUNT_CODES_MAX = 50;

/** The API's bounds on the frequency limit. */
export const DISCOUNT_FREQUENCY_COUNT_MAX = 1000;

export interface SalesAgentSettings {
	/** Controls automatic order syncing for Performance only. */
	performanceEnabled?: boolean;
	buId: number | null;
	/** The selected brand has no settings of its own; these are the account's. */
	inherited: boolean;
	disclaimer: Disclaimer;
	/** Shown under the brand name in the chat. Empty: the chat shows DEFAULT_SUBTITLE. */
	subtitle: string;
	/** Shown as tappable pills when a chat starts; set here, not generated. */
	starterQuestions: string[];
	discount: DiscountSettings;
	updatedAt: string | null;
}

/**
 * Any part can be saved on its own; the others keep their values. The Settings tab sends its
 * three parts, the Discount Limits tab only the discount.
 */
export interface UpdateSalesAgentSettingsRequestDto {
	performanceEnabled?: boolean;
	disclaimer?: Disclaimer;
	subtitle?: string;
	starterQuestions?: string[];
	discount?: DiscountSettings;
}
